import {
  OrderFulfillmentType,
  OrderSource,
  OrderType,
} from "~/entities/orderEntities";
import type { SiteListObject } from "~/entities/siteEntities";

/**
 * Catering entry helpers — the single source of truth for "does this org / site
 * offer catering" and "which site is the catering front door".
 *
 * Used by:
 *  - the shareable `/catering` link (routes/$orderSource._index.tsx),
 *  - the Catering nav items (MenuBarTop / MenuBarSide via useCateringEntry),
 *  - the /sites catering link (routes/sites.tsx),
 *  - the StoreHeader experience switch (current-site check).
 */

/** A site offers catering when it has any active catering order type. */
export const siteHasCatering = (orderTypes: OrderType[] | undefined): boolean =>
  (orderTypes ?? []).some(
    (o) => o.active && o.source === OrderSource.CATERING,
  );

/**
 * A site has *available* catering pickup when it has an active catering
 * COLLECTION order type whose code is present in `serviceSettings`. The picker's
 * serviceSettings are pre-filtered to records with non-empty availability, so a
 * matching record is a reliable "currently orderable" signal.
 */
const siteHasAvailableCateringPickup = (site: SiteListObject): boolean =>
  (site.orderTypes ?? []).some(
    (o) =>
      o.active &&
      o.source === OrderSource.CATERING &&
      o.fulfillmentType === OrderFulfillmentType.COLLECTION &&
      site.serviceSettings?.some((ss) => ss.orderTypeCode === o.code),
  );

/**
 * Resolve the single best catering "front door" site for an org: prefer one with
 * available catering pickup, fall back to any site with an active catering order
 * type (so the entrance still shows during partial provisioning), else `null`
 * when the org offers no catering at all.
 */
export const resolveCateringEntrySite = (
  sites: SiteListObject[] | undefined,
): SiteListObject | null => {
  if (!sites?.length) return null;
  return (
    sites.find(siteHasAvailableCateringPickup) ??
    sites.find((s) => siteHasCatering(s.orderTypes)) ??
    null
  );
};
